import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';
import { TypeOfSourceRequest } from '@base/enums/messages.enum';
import { SendSourcingRequestParams } from '@base/models/requests/messages.res';
import { MeetService } from '@base/services/private/meet.service';
import { ModalService } from '@base/services/private/modal.service';
import { ModalSentSuccessComponent } from '@base/components/private/modals/modal-sent-success/modal-sent-success.component';
import { ProductOfCompany, Products } from '@base/models/responses/product-of-company.res';
import { ModalHeaderComponent } from '@base/components/private/modals/modal-header/modal-header.component';
import { ModalBodyComponent } from '@base/components/private/modals/modal-body/modal-body.component';
import { DynamicImportLoaderService } from '@base/services/private/dynamic-import.service';
import { TranslatePipe } from '@base/pipes/private/translate.pipe';
import { SkeletonModalProductInquiryViaExhibitorComponent } from '@base/components/private/skeleton/skeleton-modal-product-inquiry-via-exhibitor/skeleton-modal-product-inquiry-via-exhibitor.component';
import { GlobalService } from '@base/services/private/global.service';
import { delay, finalize, Subscription } from 'rxjs';
import { StatusResponse } from '@base/enums/status-response.enum';
import { ModalFooterComponent } from '@base/components/private/modals/modal-footer/modal-footer.component';

// local enum for active button states
enum ActiveButton {
  SEND_PRODUCT_INQUIRY = 'send-product-inquiry',
}

@Component({
  selector: 'app-modal-product-inquiry-via-exhibitor',
  imports: [
    ModalHeaderComponent,
    ModalBodyComponent,
    forwardRef(() => TranslatePipe),
    SkeletonModalProductInquiryViaExhibitorComponent,
    ModalFooterComponent,
  ],
  providers: [forwardRef(() => TranslatePipe)],
  templateUrl: './modal-product-inquiry-via-exhibitor.component.html',
  styleUrl: './modal-product-inquiry-via-exhibitor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalProductInquiryViaExhibitorComponent implements OnInit, OnDestroy {
  @ViewChildren('cardProducts', { read: ViewContainerRef }) cardProducts!: QueryList<ViewContainerRef>;
  private readonly modalSrv = inject(ModalService);
  private readonly meetSrv = inject(MeetService);
  private readonly translatePipe = inject(TranslatePipe);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly dynamicImportLoaderSrv = inject(DynamicImportLoaderService);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly globalSrv = inject(GlobalService);

  @Input() modalId = '';
  @Input() isCloseAll = false;
  @Input() exhibitorId = '';

  activeButton = '';
  chatroomId = '';

  isShowTabPlaceholder = true;
  products: ProductOfCompany = {
    data: [],
  };
  productsSelected: string[] = [];
  componentInstance: ComponentRef<unknown>[] | null[] = [];
  isHttpRequestRunning = false;
  httpRequestObserver$: Subscription | null = null;

  ActiveButton = ActiveButton;

  ngOnDestroy(): void {
    this.httpRequestObserver$?.unsubscribe();
    this.isHttpRequestRunning = false;
    this.globalSrv.destroyModalRef(this.modalId);
  }

  ngOnInit(): void {
    this.getProducts();
    this.listenHttpRequestState();
  }

  /**
   * Listens for HTTP request state changes and updates the component state accordingly.
   * @returns void
   */
  listenHttpRequestState(): void {
    this.httpRequestObserver$ = this.globalSrv.httpRequestObserver().subscribe({
      next: (res) => {
        this.isHttpRequestRunning = res;
        this.triggerChangeDetection();
      },
    });
  }

  /**
   * Closes the modal using the provided modalId.
   * @returns void
   */
  close(): void {
    this.modalSrv.close(this.modalId);
  }

  /**
   * Fetches the list of products for the current exhibitor.
   * Updates the products list and triggers rendering once data is loaded.
   * @returns void
   */
  getProducts(): void {
    this.meetSrv
      .getProductsOfCompany(this.exhibitorId)
      .pipe(
        delay(700), // delay for better UX with skeleton loader
        finalize(() => {
          setTimeout(() => {
            this.isShowTabPlaceholder = false;
            this.triggerChangeDetection(); // Need to trigger change detection before rendering products to avoid ViewChilden undefined error
            this.renderProducts();
          }, 300);
        })
      )
      .subscribe({
        next: (res) => {
          this.products = res;
        },
        error: (err) => {
          console.error('Error fetching products:', err);
        },
      });
  }

  /**
   * Dynamically renders product cards for the exhibitor.
   * Loads product components asynchronously, sets inputs, and inserts them into the view.
   * @returns void
   */
  renderProducts() {
    this.products.data.forEach(async (item, index: number) => {
      this.componentInstance[index] = await this.dynamicImportLoaderSrv.loadCardProduct(this.viewContainerRef);
      if (this.componentInstance[index]) {
        this.componentInstance[index].setInput('product', item);
        this.componentInstance[index].setInput('isShowBookmarkCta', false);
        this.componentInstance[index].setInput('productInquiry', {
          selected: this.productsSelected[index] ? true : false,
        });

        if (this.cardProducts) {
          this.cardProducts.get(index)?.clear();
          this.cardProducts.get(index)?.insert(this.componentInstance[index].hostView);
        }
      }
    });
    this.triggerChangeDetection();
  }

  /**
   * Shows a modal warning if the account is a preview account and status response is -2.
   */
  showModalWarning(): void {
    this.close();
    this.modalSrv.showModalWarningForPreviewAccount();
    return;
  }

  /**
   * Sends a product inquiry request for the selected products of the exhibitor.
   * Constructs the payload with exhibitorId, selected products, and inquiry text.
   * Shows a success modal if the request is successful, otherwise shows a failure modal.
   * @returns void
   */
  sendProductInquiry(): void {
    const payload: SendSourcingRequestParams = {
      company_id: this.exhibitorId,
      text: `${this.translatePipe.transform('I_HAVE_A_PRODUCT_INQUIRY')}: {{products}}`,
      type: TypeOfSourceRequest.PRODUCT_INQUIRY_VIA_COMPANY,
      products_id: this.productsSelected,
    };
    this.activeButton = ActiveButton.SEND_PRODUCT_INQUIRY;
    this.meetSrv
      .sendSourcingRequest(payload)
      .pipe(
        finalize(() => {
          this.activeButton = '';
        })
      )
      .subscribe({
        next: (res) => {
          if (res.status === StatusResponse.SUCCESS) {
            this.chatroomId = res.chatroom_id;
            this.showMessageSuccessRequest();
          } else if (+res.status === StatusResponse.PREVIEW_ACCOUNT) {
            // Preview account returns status -2, but success returns 'SUCCESS'; always convert to number to check for -2.
            this.showModalWarning();
          } else if (+res.status === StatusResponse.OWN_COMPANY) {
            this.showFailedOwnCompanyRequest();
          } else {
            this.showFailedRequest();
          }
        },
        error: (err) => {
          console.error('Error sending sourcing request:', err);
          this.showFailedRequest();
        },
      });
  }

  /**
   * Displays a modal indicating that the product inquiry request has failed.
   * @returns void
   */
  showFailedRequest(): void {
    this.close();
    this.modalSrv.showModalFailedRequest();
  }

  /**
   * Displays a modal indicating that the product inquiry cannot be sent to the initiator's own company.
   * @returns void
   */
  showFailedOwnCompanyRequest(): void {
    this.close();
    this.modalSrv.showModalFailedRequest({
      addsData: {
        header: 'HOLD_ON',
        description: 'UNABLE_TO_SEND_TO_YOUR_OWN_COMPANY',
      },
    });
  }

  /**
   * Displays a success modal after the product inquiry request has been sent successfully.
   * Uses the chatroomId to populate modal data.
   * @returns void
   */
  showMessageSuccessRequest(): void {
    this.close();
    const data: Partial<ModalSentSuccessComponent> = {
      chatroomId: this.chatroomId,
    };

    this.modalSrv.showModalSuccessRequest({
      addsData: data,
    });
  }

  /**
   * Selects or deselects a product for inquiry based on user interaction.
   * Updates the productsSelected array and product card UI state.
   * @param item - The product being selected or deselected.
   * @param index - The index of the product in the products list.
   * @returns void
   */
  selectProduct(item: Products, index: number): void {
    const idx = this.productsSelected.findIndex((id) => id === item.product_id);
    if (idx > -1) {
      this.productsSelected.splice(idx, 1);
      this.componentInstance[index]?.setInput('productInquiry', {
        selected: false,
      });
    } else {
      this.productsSelected.push(item.product_id);
      this.componentInstance[index]?.setInput('productInquiry', {
        selected: true,
      });
    }
    this.triggerChangeDetection();
  }

  /**
   * Triggers change detection for the component.
   */
  triggerChangeDetection(): void {
    this.changeDetectorRef.detectChanges();
    this.changeDetectorRef.markForCheck();
  }
}
